/**
 * The chart's data contract — and the gate that decides whether a page of
 * bars is allowed to become pixels.
 *
 * **This file imports no charting library.** That is deliberate: it is the
 * vocabulary the *app* speaks to a chart in, so it survives the library
 * being replaced, and it is the vocabulary the tests assert in. `PriceChart`
 * translates it into KLineChart's `setDataLoader` and nothing else does.
 *
 * ── Why the gate exists ───────────────────────────────────────────────
 *
 * Measured, real headless Chrome over CDP, KLineChart v10.0.2 at 1400x800:
 * feeding bars 3,600,000 ms apart while `chart.getPeriod()` returned
 * `{type:'minute', span:15}` produced **0 page errors, 0 console warnings
 * and 51,434 drawn pixels** — a perfectly rendered chart of 60-minute bars
 * under a "15m" label. The library does not check bar spacing against the
 * period it was told, and nothing upstream of us ever will.
 *
 * That is exactly the defect this whole rewrite exists to remove. The chart
 * used to request all stored history and let the server compress it to
 * ~3,000 points; the measured result was 26x on 1h (a 1.1-day bar labelled
 * `1h`), 67x on 1m and 15m, 51x on 30m. Every intraday timeframe was, in
 * effect, the daily chart, and a previous audit passed it 500/500 because it
 * asserted that *a chart appeared*, not that *a 1h bar is one hour wide*.
 *
 * So the guard is ours, it runs on **every** page, and it fails closed: a
 * chart that cannot prove its bars are real shows an error, not a picture.
 * A believable wrong chart is worse than no chart — this platform exists to
 * take a trading decision off a human, and a composite bar under a timeframe
 * label is a lie he would act on.
 */

import type { EpochSeconds, Timeframe } from '@/api/types';
import { TIMEFRAME_SECONDS } from '@/api/types';

// ══════════════════════════════════════════════════════════════════════
//  The bar
// ══════════════════════════════════════════════════════════════════════

/**
 * One candle. `t` is epoch **seconds** — the same integer the API sends.
 *
 * Defined here rather than in `PriceChart` because the feed contract is the
 * thing that produces bars and `PriceChart` is one of the things that
 * consumes them. It is re-exported from `PriceChart` so no import path
 * outside this directory changes.
 */
export interface ChartBar {
  t: EpochSeconds;
  open: number;
  high: number;
  low: number;
  close: number;
  volume: number;
}

// ══════════════════════════════════════════════════════════════════════
//  The request/response contract
// ══════════════════════════════════════════════════════════════════════

/**
 * Which way the chart is reaching.
 *
 * **TradingView's vocabulary, not KLineChart's**, and the difference is the
 * single easiest thing to get wrong here. KLineChart calls older history
 * `'forward'` and newer data `'backward'` — inverted from every intuition —
 * and a chart that mixes the two appends ancient bars to the right-hand
 * edge. It still renders. It looks like data corruption rather than a naming
 * bug, and it is the reason the translation happens in exactly one place
 * (`PriceChart`'s adapter) against this enum.
 */
export type BarWindowDirection =
  /** First page. The live edge; `cursor` is null. */
  | 'initial'
  /** Older history — the user panned left. Bars are PREPENDED. */
  | 'older'
  /** Newer bars — the user panned right past the loaded edge. APPENDED. */
  | 'newer';

export interface BarWindowRequest {
  symbol: string;
  timeframe: Timeframe;
  direction: BarWindowDirection;
  /**
   * Epoch seconds. **Exclusive** bound, and null on `initial`.
   *
   * For `older` it is the open time of the oldest bar already loaded, and
   * the response must not contain it. Measured against the live API at all
   * ten timeframes: requesting page 2 with `end` = page 1's oldest bar gives
   * a seam gap of exactly one interval and zero duplicate timestamps.
   */
  cursor: EpochSeconds | null;
  /**
   * Bars wanted. Honour this over any time range.
   *
   * TradingView's rule, and ours for the same reason: a range is a guess at
   * a bar count once a series has gaps in it, and the chart needs a screen
   * full of bars, not a screen full of hours.
   */
  countBack: number;
}

export interface BarWindowResponse {
  /** Ascending, unique, and **exactly one interval apart**. Validated. */
  bars: ChartBar[];
  /** The store holds bars older than `bars[0]`. Drives further paging. */
  hasOlder: boolean;
  /** The store holds bars newer than the last one. False outside replay. */
  hasNewer: boolean;
}

export interface BarStreamRequest {
  symbol: string;
  timeframe: Timeframe;
  /**
   * One bar, live. Same timestamp as the last bar replaces it in place; a
   * newer timestamp appends. Nothing else in the series is touched.
   */
  onBar: (bar: ChartBar) => void;
}

/**
 * Everything the chart needs to load and follow a series.
 *
 * `fetchWindow` **must always settle** — resolve or reject, never hang.
 * KLineChart sets an internal `_loading` flag before calling the loader and
 * only the loader's callback clears it, so a promise that never settles
 * wedges history loading permanently, silently, for the life of the chart.
 */
export interface ChartDataFeed {
  fetchWindow: (request: BarWindowRequest) => Promise<BarWindowResponse>;
  /** Optional live tail. Returns its own unsubscribe. */
  subscribe?: (request: BarStreamRequest) => () => void;
}

/** What the chart currently holds, reported to the page as pages land. */
export interface LoadedWindow {
  symbol: string;
  timeframe: Timeframe;
  /** The merged series, oldest first. Uniform spacing is guaranteed. */
  bars: ChartBar[];
  /** Open time of the oldest loaded bar. */
  from: EpochSeconds;
  /** Open time of the newest loaded bar. */
  to: EpochSeconds;
  /** Older bars exist in the store beyond what is loaded. */
  hasOlder: boolean;
  /** Pages fetched for this (symbol, timeframe) since the last reset. */
  pages: number;
  /**
   * Bars the store does not hold, inside the loaded range.
   *
   * Reported so a hole can be *shown* rather than silently spanned. Zero for
   * every series in this store except BTCUSDT and ETHUSDT 12h, which are
   * each missing 2018-02-08T12:00Z — 1 of 6,294, measured out of the parquet
   * files.
   *
   * **-1 means "could not be verified"**, which is not the same as none.
   */
  holes: number;
}

// ══════════════════════════════════════════════════════════════════════
//  Errors — distinct types, because they mean different things
// ══════════════════════════════════════════════════════════════════════

/** Base for "these numbers must not be drawn". */
export class ChartDataError extends Error {}

/**
 * A page whose bars are not one timeframe interval apart.
 *
 * Carries the numbers, not an adjective: which index, which two timestamps,
 * what the gap was and what it should have been. A report of this that says
 * "bad data" is useless at 2am.
 */
export class BarIntervalError extends ChartDataError {
  readonly timeframe: Timeframe;
  readonly index: number;
  readonly gapSeconds: number;
  readonly expectedSeconds: number;

  constructor(params: {
    timeframe: Timeframe;
    index: number;
    previous: EpochSeconds;
    current: EpochSeconds;
    gapSeconds: number;
    expectedSeconds: number;
    detail?: string;
  }) {
    super(
      `${params.timeframe} page has a ${params.gapSeconds}s gap at index ${params.index} ` +
        `(bar ${params.previous} -> ${params.current}); expected exactly ` +
        `${params.expectedSeconds}s. ` +
        (params.detail === undefined ? '' : `${params.detail}. `) +
        `Refusing to draw composite bars under a ${params.timeframe} label.`,
    );
    this.name = 'BarIntervalError';
    this.timeframe = params.timeframe;
    this.index = params.index;
    this.gapSeconds = params.gapSeconds;
    this.expectedSeconds = params.expectedSeconds;
  }
}

/**
 * The server aggregated. `downsample_factor` came back greater than 1.
 *
 * Separate from `BarIntervalError` because it is caught one layer earlier —
 * at the fetch, from the response metadata, before a single bar is examined
 * — and because it names the exact original bug. `limit` and `max_points`
 * are separate query parameters and the broken combination is still the
 * endpoint's default, so this is reachable by anyone who forgets to pin them
 * together.
 */
export class AggregatedBarsError extends ChartDataError {
  readonly factor: number;

  constructor(params: { symbol: string; timeframe: Timeframe; factor: number }) {
    super(
      `${params.symbol} ${params.timeframe} came back at downsample_factor ` +
        `${params.factor} — every bar is ${params.factor} stored bars folded ` +
        `together. Pin max_points equal to limit. Refusing to draw.`,
    );
    this.name = 'AggregatedBarsError';
    this.factor = params.factor;
  }
}

// ══════════════════════════════════════════════════════════════════════
//  The gate
// ══════════════════════════════════════════════════════════════════════

/** What a page looked like once it had been checked. */
export interface BarSpacingReport {
  /** Consecutive pairs exactly one interval apart. */
  exact: number;
  /**
   * Consecutive pairs a whole number of intervals apart, but more than one
   * — a bar the store does not hold.
   *
   * A hole is NOT the defect this gate exists for and is not fatal, but it
   * is counted and reported rather than smoothed. Measured in this store:
   * BTCUSDT and ETHUSDT 12h are each missing exactly one bar,
   * 2018-02-08T12:00Z, out of 6,294 — and nothing else across 20 series.
   */
  holes: number;
}

/**
 * Prove a page really is bars of this timeframe, or throw.
 *
 * **The invariant is: the most common distance between consecutive bars is
 * exactly one interval.** That is the property the original defect
 * destroyed and a missing bar does not. Under the old `max_points`
 * compression *every* gap was 26 or 38 or 67 intervals — the modal gap was
 * wrong, on every page, which this refuses. A store that is missing one 12h
 * candle from February 2018 has a modal gap of exactly one interval, and
 * refusing to draw eight years of BTCUSDT over it would be M4 broken in the
 * other direction.
 *
 * Three rules, in order of how impossible they are:
 *
 * 1. **Strictly ascending.** A non-positive gap is a duplicate or a
 *    mis-sorted page. There is no benign reading; throw.
 * 2. **Every gap is a whole number of intervals.** A gap of 5,400s on a 1h
 *    chart is not a missing bar, it is a bar that is not on the timeframe's
 *    grid — the boundaries are wrong and nothing on the page can be trusted.
 * 3. **Exact gaps outnumber holes.** This is the aggregation test, written
 *    as a definition rather than a tuned threshold: if most consecutive
 *    bars are further apart than the label claims, the page is not that
 *    timeframe. Skipped for a two-bar page (a seam) where "most" has no
 *    meaning.
 *
 * Cost is one subtraction, one modulo and two comparisons per bar — ~1,750
 * of them on an initial page, noise next to the fetch it followed.
 */
export function verifyBarSpacing(
  bars: readonly ChartBar[],
  timeframe: Timeframe,
): BarSpacingReport {
  const expected = TIMEFRAME_SECONDS[timeframe];
  if (expected === undefined) {
    throw new ChartDataError(`unknown timeframe ${timeframe} — no interval to check against`);
  }
  let exact = 0;
  let holes = 0;
  let firstHole: { index: number; gap: number } | null = null;

  for (let i = 1; i < bars.length; i += 1) {
    // Non-null by the loop bound; indexing is hot enough here that the
    // optional-chaining spelling shows up in a profile.
    const previous = bars[i - 1] as ChartBar;
    const current = bars[i] as ChartBar;
    const gap = current.t - previous.t;

    if (gap <= 0 || gap % expected !== 0) {
      throw new BarIntervalError({
        timeframe,
        index: i,
        previous: previous.t,
        current: current.t,
        gapSeconds: gap,
        expectedSeconds: expected,
      });
    }
    if (gap === expected) {
      exact += 1;
    } else {
      holes += 1;
      firstHole ??= { index: i, gap };
    }
  }

  if (bars.length > 2 && holes >= exact && firstHole !== null) {
    const previous = bars[firstHole.index - 1] as ChartBar;
    const current = bars[firstHole.index] as ChartBar;
    throw new BarIntervalError({
      timeframe,
      index: firstHole.index,
      previous: previous.t,
      current: current.t,
      gapSeconds: firstHole.gap,
      expectedSeconds: expected,
      detail:
        `${holes} of ${holes + exact} gaps are wider than one interval — ` +
        `these are not ${timeframe} bars`,
    });
  }
  return { exact, holes };
}

// ══════════════════════════════════════════════════════════════════════
//  How many bars to ask for
// ══════════════════════════════════════════════════════════════════════

/**
 * KLineChart's own floor on bar spacing, read from
 * `dist/index.esm.js` (`_layoutOptions.barSpaceLimit = {min: 1, max: 50}`).
 *
 * This — not the default spacing — is what bounds how many bars a viewport
 * can be made to demand. `DEFAULT_BAR_SPACE` is 10, so a 1400px chart shows
 * ~116 bars at rest (measured) and up to ~1,150 fully zoomed out.
 */
const MIN_BAR_SPACE_PX = 1;

/**
 * Headroom beyond one fully-zoomed-out screen: the right-hand offset the
 * chart reserves, plus a screen of pan before the next page is needed.
 *
 * Measured basis for sizing from the *widest* viewport rather than the
 * default zoom: with a 1024-bar viewport, an init page of 200 produced 6
 * sequential `getBars` calls and a page of 500 produced 3, because
 * KLineChart allows only one load in flight. A page of 1,500 produced 1.
 * Under-sizing the page does not fetch less — it fetches the same data in
 * six round trips, which is what a pan feels like when it stutters.
 */
const VIEWPORT_MARGIN_BARS = 350;

/** Below this a request is not worth its round trip. Also the API's floor. */
const MIN_COUNT_BACK = 300;

/**
 * Ceiling on one page. Measured: 1,500 bars = 85.7 KB, 8.0 ms warm, 2.7 ms
 * of server time; 6,000 bars = 343.9 KB. Payload is linear in the answer
 * (G2), so the cap is about latency, not about the server coping.
 */
const MAX_COUNT_BACK = 5_000;

/**
 * Bars to request for a chart this wide.
 *
 * Sized from the width the viewport can reach, not the width it opens at.
 * At 1400px this is 1,750; at 3440px it is 3,790. The alternative — sizing
 * from the default 10px bar spacing — asks for ~140 bars and then discovers
 * it needs the rest one blocking request at a time.
 */
export function countBackForViewport(widthPx: number): number {
  const usable = Number.isFinite(widthPx) && widthPx > 0 ? widthPx : 1_400;
  const bars = Math.ceil(usable / MIN_BAR_SPACE_PX) + VIEWPORT_MARGIN_BARS;
  return Math.min(MAX_COUNT_BACK, Math.max(MIN_COUNT_BACK, bars));
}

/**
 * How much history one chart may accumulate before it stops asking.
 *
 * 20,000 bars is 831 KB of indicator payload at the measured worst case and
 * about 2 MB of JavaScript objects — but say the number that matters: on 1d
 * it is 54 years (the entire store, several times over) and on **1m it is
 * 13.9 days**, which a user panning through an intraday move will reach.
 * When he does, paging stops and the chart says so rather than pretending
 * the history ended. Raise it per surface with `PriceChart`'s `maxBars`.
 */
export const DEFAULT_MAX_LOADED_BARS = 20_000;

export { MAX_COUNT_BACK, MIN_COUNT_BACK };
